import type { DemoDoc, NormRect, PhoneTrack, Step, Transition } from './demo';
import type { Layout } from './geometry';
import { resolvePresenterSettings } from './presenter';
import { nextIdFor, resolveLinearPath } from './path';

/** The path Watch and every export take (`./path`), for the player's clock (phase C). */
export { nextIdFor, resolveLinearPath };
import { unionRects } from './geometry';
import { showcasePlan } from './showcase';
import {
  CAMERA_SETTLE_MS,
  panDuration,
  stepRest,
  sameLayout,
  stepCamera,
  fallbackShots,
  guideBeats,
  guidedStep,
  cuePlanDesktop,
  desktopEntry,
  guideHolds,
  type CuePlan,
  isStoryViewport,
  type StoryBeat,
  type TimedCue,
  overviewFrame,
  overviewLayout,
  shotLayout,
  STORY_END_HOLD_MS,
  phonePlan,
  storyBeats,
  storyDoc,
  storySource,
  sameScreen,
  walkMoveDuration,
  watchEstablishHold,
  readingMs,
  SILENT_READ_FACTOR,
  type Gutter,
} from './camera';

/**
 * Step timeline planning, shared by the MP4 exporter (`services/pipeline/src/
 * export/plan.ts`) and the player. Pure; imports no zod value (the player must
 * never get zod), so it reaches `./camera`, `./path`, `./geometry` and
 * `./presenter` directly, never the package root.
 */

/** Every step is held at least this long (Track K brief). */
export const BASE_HOLD_MS = 2500;
/** Roughly how much longer a step holds per character of tooltip text. */
export const MS_PER_TOOLTIP_CHAR = 60;
/** However long the text, a hold never exceeds this. */
export const MAX_HOLD_MS = 6000;

/** The beat kept after a narration clip ends before the step moves on. The
 *  player's Watch (`apps/player/src/watch.ts`) uses this same constant, so a step
 *  holds as long in the MP4 as it does in watch mode. Narration finishing right
 *  as the step advances reads as cut off; the cursor's glide and press run
 *  inside this beat rather than after it (2026-10-14). */
export const NARRATION_TAIL_MS = 400;

/** The tap ripple plays for this long, ending exactly when the transition starts. */
export const RIPPLE_MS = 500;

/**
 * ms per unit of scroll travel (`to - from`, which is at most 1). Set above
 * `MAX_SCROLL_MS` on purpose: travel of 1 — the tall asset's entire range —
 * always clamps to the maximum, and a partial scroll is proportional below
 * that ceiling.
 */
export const SCROLL_MS_PER_UNIT = 4500;
export const MIN_SCROLL_MS = 1200;
export const MAX_SCROLL_MS = 4200;

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Hold time for a static (non-scroll) step, typing included (`typingMs`). */
export function stepHoldMs(step: Step): number {
  const typing = typingMs(step);
  if (!step.annotation) return BASE_HOLD_MS + typing;
  const forText = step.annotation.text.length * MS_PER_TOOLTIP_CHAR;
  return Math.min(MAX_HOLD_MS, Math.max(BASE_HOLD_MS, forText)) + typing;
}

/** The beat kept on finished typed text before the step moves on (shared with the player's `overlays.ts`). */
export const TYPED_PAUSE_MS = 450;

// ---- Watch mode durations (apps/player/src/watch.ts, chrome.ts, runtime.ts) ----

/** After waiting for the camera to arrive, the eye gets this beat on the target before the cursor moves. */
export const CAMERA_BEAT_MS = 300;
/** A screen with nothing to read still gets a beat before the cursor moves. */
export const DWELL_MIN_MS = 900;
/**
 * The demo's last screen, with nothing to press after it (the payoff: "Invoice sent"), is held at
 * least this long before the end card covers it. At `DWELL_MIN_MS` it was gone in about 1.3 s,
 * before it was read (2026-11-03 review).
 */
export const FINAL_HOLD_MS = 3000;
/** Reading time for a tooltip: a base plus per character, clamped. ~50 ms a
 *  character is a comfortable 200 words a minute with room to find the text. */
export const TIP_BASE_MS = 600;
export const TIP_MS_PER_CHAR = 50;
export const TIP_MIN_MS = 1600;
export const TIP_MAX_MS = 6000;
/** Muted Watch: time to find the card on a new step before reading starts. */
export const FIND_CARD_MS = 1000;
/** How long a chapter title card shows (chrome.ts) — it is read before the step is. */
export const CHAPTER_MS = 1600;
/** How long before a narrated line's end the cursor may set off for the control. */
export const GLIDE_LEAD_MS = 400;
/** The press before a tap activates; the ripple runs alongside. */
export const PRESS_MS = 170;
/** A swipe or drag stroke. */
export const STROKE_MS = 460;
/** Cursor travel: proportional to distance, never a teleport, never a crawl. */
export const GLIDE_MS_PER_PX = 1.15;
export const GLIDE_MIN_MS = 380;
export const GLIDE_MAX_MS = 900;
/** Already this close (a `look` cue left the cursor on the control): a short settle, not a glide. */
export const GLIDE_NEAR_PX = 48;
export const GLIDE_NEAR_MS = 140;
/** Scroll-through speed in Watch, in ms per CSS px of remaining travel, clamped. */
export const WATCH_SCROLL_MS_PER_PX = 2.2;
export const WATCH_SCROLL_MIN_MS = 1200;
export const WATCH_SCROLL_MAX_MS = 5000;
/** How long the bottom of a scroll is shown before the step moves on. */
export const WATCH_SCROLL_REST_MS = 350;
/** Muted Watch's callouts: time to find a card before its sentence is read. */
export const CALLOUT_FIND_MS = 600;

/** The cursor's glide time over `distancePx` CSS px. */
export function glideMs(distancePx: number): number {
  return distancePx <= GLIDE_NEAR_PX
    ? GLIDE_NEAR_MS
    : clamp(distancePx * GLIDE_MS_PER_PX, GLIDE_MIN_MS, GLIDE_MAX_MS);
}

/** Watch's scroll-through time for `remainingPx` CSS px of travel (reduced motion is 0, decided by the caller). */
export function watchScrollMs(remainingPx: number): number {
  return clamp(remainingPx * WATCH_SCROLL_MS_PER_PX, WATCH_SCROLL_MIN_MS, WATCH_SCROLL_MAX_MS);
}

/** Muted Watch: how long a callout's card stays, finding it and reading its sentence. */
export function calloutHoldMs(text: string): number {
  return CALLOUT_FIND_MS + SILENT_READ_FACTOR * readingMs(text);
}

/**
 * How long the step is shown before anything is done to it.
 *
 * `narrationMs` and `presenterMs` — the step's spoken clip and its presenter
 * (webcam) clip, whichever ones watch mode will actually play/show — are each
 * a floor, not an addition: the dwell becomes whichever is longer, the
 * ordinary reading/typing time or the longer of the two clips plus a short
 * tail, so a step never advances mid-sentence or mid-clip but a long tooltip
 * is not shortened by a brief one. The two clips are cut from the same
 * recorded window and so are ordinarily the same length; the floor is
 * `Math.max` of both anyway, belt and braces against a mismatch.
 */
export function dwellFor(
  step: Step,
  chapterCard: boolean,
  narrationMs?: number | null,
  presenterMs?: number | null,
  calloutsMs?: number | null,
  reducedMotion = false,
): number {
  let ms = DWELL_MIN_MS;
  if (step.annotation) {
    ms = clamp(TIP_BASE_MS + step.annotation.text.length * TIP_MS_PER_CHAR, TIP_MIN_MS, TIP_MAX_MS);
  }
  if (!step.next && !step.hotspots.length && !step.scroll && !step.showcase) {
    ms = Math.max(ms, FINAL_HOLD_MS);
  }
  // Text on a plain target types itself out as the step opens (overlays.ts);
  // moving on before it has finished would cut the sentence off.
  const typed = step.input && step.hotspots.find((h) => h.id === step.input!.target);
  if (typed && typed.gesture !== 'type' && !reducedMotion) {
    ms += (step.input!.text.length / step.input!.cps) * 1000 + TYPED_PAUSE_MS;
  }
  if (chapterCard) ms += CHAPTER_MS;
  const clipFloor = Math.max(narrationMs ?? 0, presenterMs ?? 0);
  if (clipFloor > 0) ms = Math.max(ms, clipFloor + NARRATION_TAIL_MS);
  // Muted, the viewer reads everything the step shows (tooltip and narration, which muted Watch
  // shows whole) at a silent reader's pace, after finding the card; the walk's clock is
  // stretched by the same factor (runtime.ts `playCues`), so it ends inside this. Not cut by
  // `TIP_MAX_MS`, which caps only the annotation-only formula above.
  if (!narrationMs && calloutsMs) {
    // The callouts one at a time, then the instruction with what they did not show.
    ms = Math.max(ms, calloutsMs);
  } else if (!narrationMs) {
    const shown = [step.annotation?.text, step.narration?.text]
      .map((t) => t?.trim() ?? '')
      .filter((t, i, all) => t && all.indexOf(t) === i)
      .join(' ');
    if (shown) ms = Math.max(ms, SILENT_READ_FACTOR * readingMs(shown) + FIND_CARD_MS);
  }
  return ms;
}

/**
 * How long a step's typed text takes to type out, its closing beat included
 * (0 for a step that types nothing): the time Watch mode adds to the step's
 * dwell for it (`apps/player/src/watch.ts` `dwellFor`), so the video types
 * it out as the phone does, after the camera has arrived.
 */
export function typingMs(step: Step): number {
  const input = step.input;
  if (!input || !input.text || !step.hotspots.some((h) => h.id === input.target)) return 0;
  return Math.round((input.text.length / (input.cps || 22)) * 1000) + TYPED_PAUSE_MS;
}

/**
 * The step's narration clip length when the export will play it (narration
 * on, a clip generated), else 0. A step's hold never ends before its clip
 * does: two clips overlapping in the mix is what made the voice jump in
 * level between steps.
 */
export function narrationHoldMs(doc: DemoDoc, step: Step): number {
  // `theme.audio` and `audioAssets` did not exist before narration shipped —
  // a document published before then has neither key at all, forever
  // (publish-time defaulting never runs again on an already-published
  // document; see CLAUDE.md "the player has no zod…"). This exporter reads
  // the same raw, un-defaulted JSON the player does, so it needs the same
  // fallback.
  if (!doc.theme.audio?.narration) return 0;
  const id = step.narration?.audio;
  const asset = id ? doc.audioAssets?.[id] : undefined;
  return asset ? asset.durationMs + NARRATION_TAIL_MS : 0;
}

/**
 * The step's presenter (webcam) clip length when the export will show it
 * (the feature is on, the step has a clip), else 0 — the same floor
 * `narrationHoldMs` is, and for the same reason: the export is a recording of
 * exactly what Watch mode does, and Watch mode's dwell is sized off whichever
 * of the two clips runs longer (`apps/player/src/watch.ts` `dwellFor`).
 */
export function presenterHoldMs(doc: DemoDoc, step: Step): number {
  const settings = resolvePresenterSettings(doc.theme.presenter);
  if (!settings.show) return 0;
  const id = step.presenter?.clip;
  const asset = id ? doc.videoAssets?.[id] : undefined;
  return asset ? asset.durationMs + NARRATION_TAIL_MS : 0;
}

/** Scroll animation time, proportional to how much of the tall asset travels. */
export function scrollDurationMs(step: Step): number {
  if (!step.scroll) return 0;
  const travel = Math.max(0, step.scroll.to - step.scroll.from);
  return Math.round(clamp(travel * SCROLL_MS_PER_UNIT, MIN_SCROLL_MS, MAX_SCROLL_MS));
}

/**
 * How the camera moves leaving a step, mirroring `runtime.ts` `goTo` under
 * `theme.motion: 'cinematic'`:
 *   - `pan`: the next step is on the same screen — no layer transition, the
 *     camera pans from this framing to the next over `panDuration`;
 *   - `reveal`: the next screen grows from this step's click, already framed;
 *   - `settle`: any other layer transition; this screen pulls back to its
 *     rest framing as it leaves, the next arrives at rest and settles on its
 *     target from halfway through the transition, running on into its hold;
 *   - `glide` (a phone story with `PlanOptions.newScreen`): the next step is
 *     on the same screen; the camera moves from this step's close-up to the
 *     next step's first framing while the two pictures crossfade (or cut, on
 *     a `none` transition) — no whole screen in between;
 *   - `none`: a static document, a scroll step, or the last step.
 */
export type CameraMove = 'none' | 'pan' | 'reveal' | 'settle' | 'glide';

export interface StepCamera {
  /** Where the camera rests: the whole screen, or the legibility zoom. */
  rest: Layout;
  /** Where it frames the step's subject. */
  target: Layout;
}

export interface PlannedStep {
  step: Step;
  index: number;
  /** True for the last step in the (linear) path — it has no outgoing transition. */
  isLast: boolean;
  isScroll: boolean;
  /** Total on-screen time for this step, before its transition out. Includes
   *  `arriveMs`, so the reading time the hold was sized for is not eaten by
   *  the camera still settling. */
  holdMs: number;
  /** ms into the hold when the tap ripple starts; null when nothing is tapped
   *  (no hotspot, or this is the last step). */
  rippleAtMs: number | null;
  /** The transition that plays leaving this step, i.e. the *next* step's own
   *  `transition` field — matching `runtime.ts` `goTo`, which reads the
   *  incoming step's transition, not the outgoing one's. */
  transitionType: Transition['type'];
  transitionMs: number;
  /** The camera move leaving this step (see `CameraMove`). */
  camera: CameraMove;
  /** This step's framings; null for a scroll step (its framing is its progress). */
  layout: StepCamera | null;
  /** ms at the start of this step's hold during which the camera is still
   *  settling from `rest` to `target` (a `settle` arrival), and how far into
   *  the settle it already was when the hold began. Both 0 otherwise. */
  arriveMs: number;
  arriveElapsedMs: number;
  /** Cumulative ms on the rendered timeline when this step's hold begins —
   *  i.e. right after the incoming transition (if any) finishes. Additive:
   *  nothing before `../export/audio.ts` read it. `startMs + holdMs` is where
   *  the outgoing transition begins; `startMs + holdMs + transitionMs` is the
   *  next step's own `startMs`. */
  startMs: number;
  /** The point a `reveal` grows from, in output px: the centre of the
   *  hotspot watch mode would press, projected through this step's target. */
  origin: { x: number; y: number } | null;
  /**
   * Phone story (a 9:16 social cut of a landscape demo with shots — the
   * player's `@mado/schema/camera` story, without the caption band): the
   * step opens on `layout.rest` (its first shot), rests there `leadMs`, then
   * pans to `layout.target` (its close-up) over `panMs`, both at the start
   * of the hold. Null otherwise — every plain export.
   */
  story: { leadMs: number; panMs: number } | null;
  /**
   * The story's close (`PlanOptions.bookends`, last step only): at the end of
   * the hold the camera pulls back to the whole screen (`layout`) over
   * `panMs` and rests there `holdMs` before the end card — both already
   * counted in `holdMs`. Null otherwise.
   */
  outro: { panMs: number; holdMs: number; layout: Layout } | null;
  /**
   * A desktop guided step (`@mado/schema/camera` `guideBeats`,
   * docs/plans/desktop-guided-camera.md): the step opens on `layout.rest`
   * (the whole screen, or its first region) and at each key's `atMs` into
   * the hold the camera moves to the key's `layout` over `ms` — holds sized
   * by the narration, as Watch mode does (`guideHolds`). The last key is
   * `layout.target` (no keys: it rests on `layout.target` throughout). Null
   * otherwise: every step without `Step.view`.
   */
  guide: {
    keys: { atMs: number; ms: number; layout: Layout }[];
    /** Narration cues' marks (`cuePlan`): each drawn from `startMs` to `endMs` into the hold. */
    cues?: TimedCue[];
  } | null;
}

/**
 * How a story render is planned. Omitted (every plain export, and every
 * caller before the story card existed), `planSteps` behaves exactly as it
 * did: the story gate is `tellsStory(doc, target)` and beats are laid out in
 * `target`'s own pixels.
 */
export interface PlanOptions {
  /** Tell the story regardless of `target`'s shape (the story card's window
   *  can be squarer than `isStoryViewport` allows; the canvas decided). */
  story?: boolean;
  /** `target`'s width in phone CSS px: story beats are laid out on that phone
   *  and scaled up, so the legibility floor and the close-up's text cap mean
   *  what they mean on a phone, not in video pixels. */
  refW?: number;
  /** Open the first step on the whole screen and pull back to it after the
   *  last, as the player does on a phone. */
  bookends?: boolean;
  /**
   * Did the screen change arriving at `step` from `prev` (the step before it
   * in this render; null on the first)? Given, a story step on the screen the
   * viewer was already looking at drops its lead-ins (the whole screen, a
   * result shot) and the camera glides from the last target straight to the
   * next (`CameraMove` `glide`); only a changed screen — a new page, a
   * dialog over the old one — is shown whole first. Measured in the pixels
   * by the caller (`render.ts`); absent, every step plays its lead-ins.
   */
  newScreen?: (step: Step, prev: Step | null) => boolean;
  /**
   * Where a story close-up may cut a step's screen cleanly
   * (`@mado/schema/camera` `Gutter`), for a step without shots of its own
   * (one with shots reads them off its shots, as the player does). `rows` is
   * the band of the screen its close-up shows. Found in the pixels by the
   * caller; absent, no snapping.
   */
  gutters?: (step: Step, rows: { y0: number; y1: number }) => readonly Gutter[] | undefined;
}

/**
 * Does this render tell the phone story? Only a portrait canvas, a
 * landscape source and a story document (`storyDoc`: shots, or a landscape
 * web/desktop capture without any, which walks `fallbackShots` exactly as
 * the player does) — never the plain export, which is the source's own shape.
 */
export function tellsStory(doc: DemoDoc, target: { w: number; h: number }): boolean {
  return storyDoc(doc) && isStoryViewport(target, storySource(doc));
}

/** A rect this close to the whole screen says nothing about where to look. */
const REGION_NEAR_WHOLE = 0.9;
const nearWholeRect = (r: NormRect): boolean =>
  r.w >= REGION_NEAR_WHOLE && r.h >= REGION_NEAR_WHOLE;

/**
 * The part of a screen a step is about, when its author or the director said
 * so: the union of its `shots` (none of them the whole screen), else of its
 * `cues`. Null when neither names a region smaller than the screen — the
 * caller then frames the step as it always did. Pure.
 */
export function stepContentRegion(step: Pick<Step, 'shots' | 'cues' | 'scroll'>): NormRect | null {
  if (step.scroll) return null;
  const shots = unionRects((step.shots ?? []).map((s) => s.rect).filter((r) => !nearWholeRect(r)));
  if (shots && !nearWholeRect(shots)) return shots;
  const cues = unionRects((step.cues ?? []).map((c) => c.rect));
  if (cues && !nearWholeRect(cues)) return cues;
  return null;
}

/** `r` with a little air round it, kept inside the screen. */
function padRegion(r: NormRect, by = 0.03): NormRect {
  const x = Math.max(0, r.x - by);
  const y = Math.max(0, r.y - by);
  return { x, y, w: Math.min(1, r.x + r.w + by) - x, h: Math.min(1, r.y + r.h + by) - y };
}

/**
 * The target watch mode follows on a step with more than one hotspot — the
 * one whose destination is the step's own `next`, else the first. Kept
 * identical to `apps/player/src/watch.ts` `primaryHotspot` on purpose: the
 * export is a recording of exactly the path Watch mode would take, so the
 * reveal transition's origin (below) and the ripple (`frame.ts`
 * `composeRipple`) both have to land on the same hotspot Watch mode taps,
 * not just the first one drawn.
 */
export function primaryHotspotGeom(step: Step): NormRect | null {
  const main = step.next ?? null;
  const h = step.hotspots.find((x) => (x.next ?? null) === main) ?? step.hotspots[0];
  return h?.geom ?? null;
}

/** The full plan for one export: every step on the path, in order, fully timed.
 *  `target` is the pixel viewport the camera math frames against — the plain
 *  export's own capped-source size by default, or a social cut's stage rect
 *  (`socialStage`) when the caller is rendering into a fixed social canvas;
 *  either way it must match whatever `render.ts` actually draws frames at.
 *
 *  `stepIds`, when given, is a social cut's short-cut selection
 *  (`selectShortCutSteps` below): the linear path filtered down to just
 *  those ids, in path order — any id not actually on the path is ignored.
 *  Filtering happens *before* the camera/transition pass below, so two steps
 *  that become adjacent only because everything between them was cut get
 *  their transition and camera move recomputed exactly as any other two
 *  adjacent steps would — there is no separate "filtered" code path to keep
 *  in sync. Omitted (the plain export's only call shape), this function is
 *  unchanged from before social cut existed. */
export function planSteps(
  doc: DemoDoc,
  target: { w: number; h: number },
  stepIds?: readonly string[] | null,
  opts: PlanOptions = {},
): PlannedStep[] {
  const fullPath = resolveLinearPath(doc);
  const path = stepIds ? fullPath.filter((s) => stepIds.includes(s.id)) : fullPath;
  const cinematic = doc.theme.motion === 'cinematic';
  const story = opts.story ?? tellsStory(doc, target);
  // Story beats are laid out on the phone (`refW` wide) and scaled up by `k`;
  // without `refW`, `k` is 1 and the phone is the target itself, as before.
  const k = story && opts.refW ? target.w / opts.refW : 1;
  const phone = { w: target.w / k, h: target.h / k };
  const picture = { x: 0, y: 0, ...phone };
  const up = (l: Layout): Layout =>
    k === 1
      ? l
      : {
          ...l,
          scale: l.scale * k,
          tx: l.tx * k,
          ty: l.ty * k,
          containScale: l.containScale * k,
          textPx: l.textPx * k,
        };
  /** Two steps whose screens are the same size: a camera can move across from one to the other. */
  const sameShape = (a: Step, b: Step): boolean => {
    const A = doc.assets[a.asset];
    const B = doc.assets[b.asset];
    return !!A && !!B && A.w === B.w && A.h === B.h;
  };
  /** Does this step open on its lead-ins (`PlanOptions.newScreen`)? */
  const leads = (index: number): boolean => {
    if (!opts.newScreen || index === 0) return true;
    const prev = path[index - 1]!;
    return !!prev.scroll || opts.newScreen(path[index]!, prev);
  };
  // The whole screen of a phone story is the page's content box (as in the player).
  const wholeFrame = overviewFrame(doc);
  /**
   * A story step's planned phone track on its narration's clock (docs/plans/phone-track.md):
   * the same frames the player shows on a phone, laid out in the phone window (`up` scales
   * them). Null without a track: the walk below, as before.
   */
  const phoneFor = (step: Step) => {
    if (!story || step.scroll || !step.phone) return null;
    const asset = doc.assets[step.asset];
    if (!asset) return null;
    const narration = narrationHoldMs(doc, step);
    return phonePlan(
      step as Step & { phone: PhoneTrack },
      picture,
      asset,
      narration > 0 ? narration - NARRATION_TAIL_MS : null,
      phone,
    );
  };
  const beatsFor = (step: Step, index: number) => {
    const planned = phoneFor(step);
    if (planned) {
      return planned.beats.map((b): StoryBeat => ({
        reason: b.reason === 'target' ? 'target' : 'establish',
        layout: b.layout,
        holdMs: 0,
      }));
    }
    const asset = story && !step.scroll ? doc.assets[step.asset] : undefined;
    if (!asset) return null;
    const overview = !!opts.bookends && index === 0;
    // A step with nothing to press and no focus would show the whole screen,
    // which in a portrait or square window is a thin strip. When its shots or
    // cues name where to look, frame that instead (the demo's opening keeps
    // its whole-screen establishing shot).
    if (!overview && !step.hotspots.length && !step.focus) {
      const region = stepContentRegion(step);
      if (region) {
        const layout = shotLayout(picture, asset, padRegion(region));
        if (layout.scale > layout.containScale * 1.05) {
          return [{ reason: 'target', layout, holdMs: 0 } as StoryBeat];
        }
      }
    }
    let gutters: readonly Gutter[] | undefined;
    if (opts.gutters && !step.shots?.length) {
      const close = fallbackShots(step, asset, picture).at(-1)!.rect;
      gutters = opts.gutters(step, { y0: close.y, y1: close.y + close.h });
    }
    const beats = storyBeats(step, picture, asset, doc.theme.motion, {
      overview,
      lead: leads(index),
      // A changed screen is shown whole first, then the camera moves to the
      // target; an overview step stays whole (docs/plans/phone-overview.md).
      establish: index > 0 && leads(index),
      frame: wholeFrame,
      ...(gutters ? { gutters } : {}),
    });
    return openOnFirstCue(step, index, beats);
  };
  /**
   * A step with narration cues opens on the area its first cue names instead of
   * the whole screen: a wide capture shown whole in a portrait window is a small
   * strip nobody can read while the line is spoken. The glide to the target is
   * unchanged. The demo's own opening and steps about the page stay whole.
   */
  const openOnFirstCue = (step: Step, index: number, beats: StoryBeat[]): StoryBeat[] => {
    const first = beats[0];
    const cue = step.cues?.[0];
    const asset = doc.assets[step.asset];
    if (!first || first.reason !== 'establish' || !cue || !asset || index === 0) return beats;
    if (opts.bookends && index === 0) return beats;
    if (nearWholeRect(cue.rect)) return beats;
    const layout = shotLayout(picture, asset, padRegion(cue.rect));
    if (layout.scale <= layout.containScale * 1.05) return beats;
    return [{ ...first, layout }, ...beats.slice(1)];
  };
  /**
   * Lead-in hold and pan of a story step, in ms. The video records Watch
   * mode: a whole-screen establishing shot is held over the step's
   * narration lead-in (`watchEstablishHold`), as the player holds it, and
   * every move takes a phone's pace (`walkMoveDuration`).
   */
  const storyTiming = (step: Step, index: number): { leadMs: number; panMs: number } | null => {
    // A planned phone track moves on the narration's clock (`guideTiming`), not lead and pan.
    if (phoneFor(step)) return null;
    const beats = beatsFor(step, index);
    if (!beats) return null;
    if (beats.length < 2) return { leadMs: 0, panMs: 0 };
    const first = beats[0]!;
    const last = beats[beats.length - 1]!;
    const narration = narrationHoldMs(doc, step);
    const leadMs =
      first.reason === 'establish' && !(opts.bookends && index === 0)
        ? watchEstablishHold(narration > 0 ? narration - NARRATION_TAIL_MS : null)
        : first.holdMs;
    return { leadMs, panMs: walkMoveDuration(first.layout, last.layout, phone) };
  };
  /** The pull back to the whole screen after the last step (`PlanOptions.bookends`). */
  const outroFor = (step: Step, index: number): PlannedStep['outro'] => {
    const beats = opts.bookends ? beatsFor(step, index) : null;
    if (!beats) return null;
    const from = beats[beats.length - 1]!.layout;
    // The pull-back ends on the region the step is about when it names one
    // (never wider than the screen, never closer than the close-up it leaves),
    // else on the whole screen as before.
    const asset = doc.assets[step.asset]!;
    const region = stepContentRegion(step);
    const regionLayout = region ? shotLayout(picture, asset, padRegion(region)) : null;
    const to =
      regionLayout && regionLayout.scale > regionLayout.containScale * 1.05
        ? regionLayout.scale <= from.scale
          ? regionLayout
          : from
        : overviewLayout(picture, asset, wholeFrame);
    const panMs = sameLayout(from, to) ? 0 : walkMoveDuration(from, to, phone);
    return { panMs, holdMs: STORY_END_HOLD_MS, layout: up(to) };
  };

  const guideInput = (asset: DemoDoc['assets'][string]) => ({
    viewport: target,
    asset,
    allowFocus: true,
    motion: doc.theme.motion,
  });
  /**
   * A desktop step's cue plan, at reading pace or on its clip's clock (`timed`) as Watch plays it.
   * On the screen the step before was on, it carries on from where that step's timed plan left
   * the camera (`desktopEntry`), as the player does.
   */
  const cuePlans = new Map<string, CuePlan>();
  const cuePlanAt = (step: Step, index: number, timed: boolean): CuePlan => {
    const key = `${index}:${timed}`;
    const known = cuePlans.get(key);
    if (known) return known;
    const prev = index > 0 ? path[index - 1]! : null;
    const prevEnd =
      prev && doc.assets[prev.asset] && prev.cues?.length && guidedStep(prev, doc.theme.motion)
        ? (cuePlanAt(prev, index - 1, true).beats.at(-1)?.layout ?? null)
        : null;
    const entry = desktopEntry(prev, prevEnd, step, doc.assets, doc.theme.motion);
    const narration = timed ? narrationHoldMs(doc, step) : 0;
    const plan = cuePlanDesktop(
      step,
      guideInput(doc.assets[step.asset]!),
      narration > 0 ? narration - NARRATION_TAIL_MS : null,
      entry,
    );
    cuePlans.set(key, plan);
    return plan;
  };
  /** A desktop guided step's beats (one: it rests there and nothing moves), or null (`guideBeats`). */
  const guideFor = (step: Step, index: number) => {
    if (story || !guidedStep(step, doc.theme.motion)) return null;
    const asset = doc.assets[step.asset];
    if (!asset) return null;
    if (step.cues?.length) {
      // Narration cues: the plan at reading pace says where it starts and rests.
      return cuePlanAt(step, index, false).beats.map((b): StoryBeat => ({
        reason: 'establish',
        layout: b.layout,
        holdMs: 0,
      }));
    }
    const prev = index > 0 ? path[index - 1]! : null;
    const beats = guideBeats(
      step,
      { viewport: target, asset, allowFocus: true, motion: doc.theme.motion },
      { establish: !sameScreen(prev, step, doc.assets) },
    );
    return beats;
  };
  /** Its camera keys over the hold, timed to the narration as Watch mode times them. */
  const guideTiming = (step: Step, index: number): PlannedStep['guide'] => {
    const planned = phoneFor(step);
    if (planned) {
      // The phone track's frames and the cues' marks, as the player plays them on a phone.
      const keys = planned.beats.slice(1).map((b, i) => ({
        atMs: b.atMs,
        ms: walkMoveDuration(planned.beats[i]!.layout, b.layout, phone),
        layout: up(b.layout),
      }));
      return { keys, cues: planned.cues };
    }
    const beats = guideFor(step, index);
    if (!beats) return null;
    if (step.cues?.length) {
      // The cues on the clip's clock, as Watch mode plays them (`playCues`).
      const plan = cuePlanAt(step, index, true);
      const keys = plan.beats.slice(1).map((b, i) => ({
        atMs: b.atMs,
        ms: panDuration(plan.beats[i]!.layout, b.layout, target),
        layout: b.layout,
      }));
      return { keys, cues: plan.cues };
    }
    // One beat: nothing moves, but the frame is the guided rest (an overview
    // step's whole screen), not the step's ordinary target.
    if (beats.length < 2) return { keys: [] };
    const moves = beats.slice(1).map((b, i) => panDuration(beats[i]!.layout, b.layout, target));
    const narration = narrationHoldMs(doc, step);
    const holds = guideHolds(
      beats,
      moves,
      narration > 0 ? narration - NARRATION_TAIL_MS : null,
      step.view,
    );
    const keys: { atMs: number; ms: number; layout: Layout }[] = [];
    let t = 0;
    for (let i = 1; i < beats.length; i++) {
      t += holds[i - 1]!;
      keys.push({ atMs: t, ms: moves[i - 1]!, layout: beats[i]!.layout });
      t += moves[i - 1]!;
    }
    return { keys };
  };

  const layoutFor = (step: Step, index: number): StepCamera | null => {
    if (step.scroll) return null;
    const asset = doc.assets[step.asset];
    if (!asset) return null;
    const guided = guideFor(step, index);
    if (guided) return { rest: guided[0]!.layout, target: guided[guided.length - 1]!.layout };
    if (story) {
      // The walk's first shot and its close-up; a middle lead-in (rare: a
      // result and an establish both) is passed through on the way.
      const beats = beatsFor(step, index)!;
      return { rest: up(beats[0]!.layout), target: up(beats[beats.length - 1]!.layout) };
    }
    const input = { viewport: target, asset, allowFocus: true, motion: doc.theme.motion };
    return {
      rest: stepRest(step, input),
      target: stepCamera(step, input),
    };
  };

  let arrive = { ms: 0, elapsed: 0 };
  let elapsedMs = 0;
  return path.map((step, index) => {
    const startMs = elapsedMs;
    const isLast = index === path.length - 1;
    const isScroll = !!step.scroll;
    // A showcase step plays its own panel and words (`@mado/schema/showcase`): no camera walk,
    // no cue marks, no tap, and as long as its lines take.
    const showcase = !isScroll && !!step.showcase;
    const layout = layoutFor(step, index);
    const walk = showcase ? null : storyTiming(step, index);
    const guide = isScroll || showcase ? null : guideTiming(step, index);
    const outro = isLast ? outroFor(step, index) : null;
    const lastKey = guide?.keys[guide.keys.length - 1];
    const arriveMs = walk
      ? walk.leadMs + walk.panMs
      : lastKey
        ? lastKey.atMs + lastKey.ms
        : arrive.ms;
    const arriveElapsedMs = walk || guide ? 0 : arrive.elapsed;
    // The clip starts at `startMs` (export/audio.ts) and the next one at the
    // next step's `startMs`: the hold alone has to outlast it. `MAX_HOLD_MS`
    // caps reading time, never speech. A story's pull back comes after both.
    const holdMs =
      Math.max(
        (isScroll ? scrollDurationMs(step) : stepHoldMs(step)) + arriveMs,
        narrationHoldMs(doc, step),
        presenterHoldMs(doc, step),
        showcase ? showcaseHoldMs(doc, step) : 0,
      ) + (outro ? outro.panMs + outro.holdMs : 0);
    const advances = !isLast && nextIdFor(step) !== null;
    const rippleAtMs =
      advances && !isScroll && !showcase && step.hotspots.length > 0
        ? Math.max(0, holdMs - RIPPLE_MS)
        : null;
    const next = isLast ? null : path[index + 1]!;
    // A 'none' transition is a hard cut regardless of its `ms` — the schema
    // default (fade, 260ms) only applies when a step actually asks for a fade.
    let transitionMs = next && next.transition.type !== 'none' ? next.transition.ms : 0;
    const transitionType: Transition['type'] = next ? next.transition.type : 'none';

    let camera: CameraMove = 'none';
    arrive = { ms: 0, elapsed: 0 };
    // Into or out of a showcase: the layers' own transition, no camera across it.
    if (showcase || (next && !next.scroll && next.showcase)) {
      if (transitionType === 'reveal') camera = 'reveal';
    } else if (story) {
      // A story step changes screen by its own transition (each step arrives on
      // its first shot and walks itself in): no settle or pan across steps.
      const nextBeats = next && !next.scroll ? beatsFor(next, index + 1) : null;
      const ownBeats = !isScroll ? beatsFor(step, index) : null;
      if (nextBeats && ownBeats && !leads(index + 1) && sameShape(step, next!)) {
        // Same screen: the camera glides from this target to the next one,
        // the two pictures crossfading under it if they differ.
        camera = 'glide';
        const from = ownBeats[ownBeats.length - 1]!.layout;
        const to = nextBeats[0]!.layout;
        const pan = sameLayout(from, to) ? 0 : walkMoveDuration(from, to, phone);
        transitionMs = Math.max(transitionType === 'none' ? 0 : transitionMs, pan);
      } else if (transitionType === 'reveal') camera = 'reveal';
    } else if (cinematic && next && layout) {
      const nextLayout = layoutFor(next, index + 1);
      // A guided step is arrived at on its first beat (`layout.rest`) and
      // walks itself in during its hold (`guide`), as the player's stage does.
      const nextGuided = (guideFor(next, index + 1)?.length ?? 0) > 1;
      if (nextLayout && next.asset === step.asset) {
        camera = 'pan';
        const to = nextGuided ? nextLayout.rest : nextLayout.target;
        transitionMs = sameLayout(layout.target, to) ? 0 : panDuration(layout.target, to, target);
      } else if (transitionType === 'reveal') {
        camera = 'reveal';
      } else if (
        nextLayout &&
        (!sameLayout(nextLayout.rest, nextLayout.target) || !sameLayout(layout.rest, layout.target))
      ) {
        // This screen pulls back to rest as it leaves; the next settles on
        // its target if it has one. The settle starts halfway through the
        // layer transition and runs CAMERA_SETTLE_MS; what is left after the
        // transition ends carries into the next hold (`runtime.ts` `goTo`).
        camera = 'settle';
        if (!nextGuided && !sameLayout(nextLayout.rest, nextLayout.target)) {
          const elapsed = Math.max(0, transitionMs - Math.round(transitionMs * 0.5));
          arrive = { ms: Math.max(0, CAMERA_SETTLE_MS - elapsed), elapsed };
        }
      }
    }

    let origin: { x: number; y: number } | null = null;
    const geom = layout && primaryHotspotGeom(step);
    if (geom && layout) {
      const asset = doc.assets[step.asset]!;
      const l = layout.target;
      origin = {
        x: l.tx + (geom.x + geom.w / 2) * asset.w * l.scale,
        y: l.ty + (geom.y + geom.h / 2) * asset.h * l.scale,
      };
    }

    elapsedMs += holdMs + transitionMs;

    return {
      step,
      index,
      isLast,
      isScroll,
      holdMs,
      rippleAtMs,
      transitionType,
      transitionMs,
      camera,
      layout,
      arriveMs,
      arriveElapsedMs,
      startMs,
      origin,
      story: walk,
      outro,
      guide,
    };
  });
}

/** A showcase step's time in a video: its lines on the voice's clock (or a reader's), then a beat. */
export function showcaseHoldMs(doc: DemoDoc, step: Step): number {
  if (!step.showcase) return 0;
  const clip = narrationHoldMs(doc, step);
  return showcasePlan(
    { narration: step.narration, showcase: step.showcase },
    clip > 0 ? clip - NARRATION_TAIL_MS : null,
  ).ms;
}

export function totalDurationMs(plan: PlannedStep[]): number {
  return plan.reduce((sum, p) => sum + p.holdMs + p.transitionMs, 0);
}
